import { MAX_CHUNK_SIZE, MIN_CHUNK_SIZE } from "./chunkSize";
import { buildChunkManifest, MAX_CHUNKED_OBJECT_PARTS } from "./chunkManifest";
import { createPayloadDigestOnce } from "./smallFileLane";
import { PersistedByteIntegrityError } from "./uploadChunk";

export interface UploadObjectPartDescriptor {
	ordinal: number;
	offset: number;
	byteSize: number;
	sha256: string;
}
interface UploadObjectBytes {
	clientObjectId: string;
	byteSize: number;
	contentType: string;
	partCount: number;
	manifestSha256: string;
	parts: UploadObjectPartDescriptor[];
}
export interface KnownSizeUploadObject extends UploadObjectBytes {
	sequence: 0;
	runId?: never;
	fragmentIndex?: never;
	fragmentOffset?: never;
	fragmentByteSize?: never;
}
export interface RecordingUploadObject extends UploadObjectBytes {
	runId: string;
	fragmentIndex: number;
	fragmentOffset: number;
	fragmentByteSize: number;
	sequence?: never;
}
export type UploadObjectDescriptor = KnownSizeUploadObject | RecordingUploadObject;
export interface UploadObjectPartsPage {
	clientObjectId: string;
	manifestSha256: string;
	partsPage: UploadObjectPartDescriptor[];
}
export interface UploadObjectFence {
	attemptId: string;
	generation: number;
	takeGeneration: string;
}
export interface UploadRunDeclaration {
	runId: string;
	recordingPartIndex: number;
	firstFragmentIndex: number;
}
export interface UploadRunBoundary {
	firstFragmentIndex: number;
	finalFragmentIndex: number | null;
	fragmentCount: number;
	totalBytes: number;
	finality: "confirmed" | "recovered";
}
export interface RecordingSourceSummary {
	kind: "recording";
	runCount: number;
	fragmentCount: number;
	totalBytes: number;
	finality: "confirmed" | "recovered";
}

export function uploadObjectManifestPreimage(byteSize: number, parts: readonly UploadObjectPartDescriptor[]): string {
	return JSON.stringify([
		2,
		byteSize,
		[...parts].sort((a, b) => a.ordinal - b.ordinal).map((part) => [part.ordinal, part.offset, part.byteSize, part.sha256])
	]);
}
/** Read the existing persisted byte grid; callers commit this immutable plan before requesting a target. */
export async function buildKnownSizeUploadObject(input: {
	clientObjectId: string;
	byteSize: number;
	contentType: string;
	chunkSize: number;
	readPart(offset: number, byteSize: number): Promise<Blob>;
}): Promise<KnownSizeUploadObject> {
	validateObjectIdentity(input.clientObjectId, input.contentType);
	if (input.byteSize > 5 * 1024 ** 4 || input.chunkSize > MAX_CHUNK_SIZE)
		throw new PersistedByteIntegrityError("Object exceeds storage size limits");
	const grid = buildChunkManifest(input.byteSize, input.chunkSize);
	if (!grid.ok) throw new PersistedByteIntegrityError(`Invalid object byte grid: ${grid.reason}`);
	const parts: UploadObjectPartDescriptor[] = [];
	for (const part of grid.manifest.parts) {
		const bytes = await input.readPart(part.offset, part.length);
		if (bytes.size !== part.length)
			throw new PersistedByteIntegrityError("Persisted object part does not match its immutable byte range");
		parts.push({ ordinal: part.ordinal, offset: part.offset, byteSize: part.length, sha256: await createPayloadDigestOnce(bytes)() });
	}
	const manifestSha256 = await createPayloadDigestOnce(new Blob([uploadObjectManifestPreimage(input.byteSize, parts)]))();
	return {
		clientObjectId: input.clientObjectId.toLowerCase(),
		sequence: 0,
		byteSize: input.byteSize,
		contentType: input.contentType,
		partCount: parts.length,
		manifestSha256,
		parts
	};
}

function validateObjectIdentity(clientObjectId: string, contentType: string): void {
	if (
		typeof clientObjectId !== "string" ||
		typeof contentType !== "string" ||
		!/^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(clientObjectId) ||
		!contentType ||
		contentType.length > 255 ||
		/[\r\n]/.test(contentType)
	)
		throw new PersistedByteIntegrityError("Invalid object identity or content type");
}

/**
 * Plan one complete retained media fragment, yielding one storage object at a time.
 * The owner persists each offset's identity and plan before admission; this planner neither owns nor deletes source bytes.
 */
export async function* planRecordingFragmentUploadObjects(input: {
	runId: string;
	fragmentIndex: number;
	byteSize: number;
	contentType: string;
	chunkSize: number;
	maxObjectBytes?: number;
	clientObjectIdForOffset(offset: number): Promise<string>;
	readPart(offset: number, byteSize: number): Promise<Blob>;
}): AsyncGenerator<RecordingUploadObject> {
	validateObjectIdentity(input.runId, input.contentType);
	const maxObjectBytes = input.maxObjectBytes ?? 5 * 1024 ** 4;
	if (
		!Number.isSafeInteger(input.fragmentIndex) ||
		input.fragmentIndex < 0 ||
		!Number.isSafeInteger(input.byteSize) ||
		input.byteSize < 0 ||
		!Number.isSafeInteger(input.chunkSize) ||
		input.chunkSize < MIN_CHUNK_SIZE ||
		input.chunkSize > MAX_CHUNK_SIZE ||
		!Number.isSafeInteger(maxObjectBytes) ||
		maxObjectBytes < 1 ||
		maxObjectBytes > 5 * 1024 ** 4
	)
		throw new PersistedByteIntegrityError("Invalid recording fragment coordinates or byte limits");
	// Segment before either storage limit requires larger reads than the existing platform byte policy.
	const objectByteLimit = Math.min(maxObjectBytes, input.chunkSize * MAX_CHUNKED_OBJECT_PARTS);
	let fragmentOffset = 0;
	do {
		const byteSize = Math.min(objectByteLimit, input.byteSize - fragmentOffset);
		const clientObjectId = await input.clientObjectIdForOffset(fragmentOffset);
		validateObjectIdentity(clientObjectId, input.contentType);
		const planned =
			byteSize === 0
				? {
						clientObjectId: clientObjectId.toLowerCase(),
						byteSize: 0,
						contentType: input.contentType,
						partCount: 0,
						manifestSha256: await createPayloadDigestOnce(new Blob([uploadObjectManifestPreimage(0, [])]))(),
						parts: []
					}
				: await buildKnownSizeUploadObject({
						clientObjectId,
						byteSize,
						contentType: input.contentType,
						chunkSize: input.chunkSize,
						readPart: (offset, length) => input.readPart(fragmentOffset + offset, length)
					});
		yield {
			clientObjectId: planned.clientObjectId,
			byteSize: planned.byteSize,
			contentType: planned.contentType,
			partCount: planned.partCount,
			manifestSha256: planned.manifestSha256,
			parts: planned.parts,
			runId: input.runId.toLowerCase(),
			fragmentIndex: input.fragmentIndex,
			fragmentOffset,
			fragmentByteSize: input.byteSize
		};
		fragmentOffset += byteSize;
	} while (fragmentOffset < input.byteSize);
}
/** The full plan stays local; huge manifests use the existing bounded admission operation. */
export function firstUploadObjectDeclaration(object: UploadObjectDescriptor): UploadObjectDescriptor {
	return { ...object, parts: object.parts.slice(0, 1000) };
}
